import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PhotoSubmissionModal from './PhotoSubmissionModal';
import './PhotoGuidelinesFlyer.css';

const PhotoGuidelinesFlyer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [fromModal, setFromModal] = useState(false);

  useEffect(() => {
    // Check if we came from the modal (via state or referrer)
    const state = location.state;
    if (state && state.fromModal) {
      setFromModal(true);
    }
  }, [location]);

  const handlePlayGame = () => {
    navigate('/');
  };

  const handleSubmitPhoto = () => {
    setShowPhotoModal(true);
  };

  const handleCloseModal = () => {
    setShowPhotoModal(false);
  };

  const handleCloseFlyer = () => {
    if (fromModal) {
      setShowPhotoModal(true);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="photo-guidelines-flyer">
      <Helmet>
        {/* Open Graph Meta Tags for Social Media */}
        <title>📸 NicoleCodeGirl Photo Submissions - Share Your Photos!</title>
        <meta name="description" content="Share your photos with the NicoleCodeGirl community! Submit your best photos to be featured in memory games with full attribution. Join our creative community!" />
        
        {/* Open Graph */}
        <meta property="og:title" content="📸 NicoleCodeGirl Photo Submissions - Share Your Photos!" />
        <meta property="og:description" content="Share your photos with the NicoleCodeGirl community! Submit your best photos to be featured in memory games with full attribution. Join our creative community!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="https://memory-game.nicolecodegirl.com/photos/photo_Contest_Flyer.png?v=3" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="NicoleCodeGirl Photo Submissions Flyer" />
        <meta property="og:site_name" content="NicoleCodeGirl" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="📸 NicoleCodeGirl Photo Submissions - Share Your Photos!" />
        <meta name="twitter:description" content="Share your photos with the NicoleCodeGirl community! Submit your best photos to be featured in memory games with full attribution. Join our creative community!" />
        <meta name="twitter:image" content="https://memory-game.nicolecodegirl.com/photos/photo_Contest_Flyer.png?v=3" />
        <meta name="twitter:site" content="@nicolecodegirl" />
        
        {/* Additional Meta Tags */}
        <meta name="keywords" content="photo sharing, memory game, NicoleCodeGirl, photo submission, community, photography, creative collaboration" />
        <meta name="author" content="NicoleCodeGirl" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="flyer-container">
        {/* Header */}
        <div className="flyer-header">
          <button className="flyer-close-btn" onClick={handleCloseFlyer}>×</button>
          <h1 className="flyer-title">📸 Theme 2 - Community Photo Sharing 📸</h1>
          <p className="flyer-subtitle">Share your photos with our community and get credit when they're featured in memory games!</p>
        </div>

        {/* Main Content */}
        <div className="flyer-content">
          <div className="submission-info">
            <h2 className="submission-heading">🎯 How Photo Sharing Works</h2>
            <div className="info-grid">
              <div className="info-card">
                <div className="info-icon">📸</div>
                <h3>Submit Photos</h3>
                <p>Share your best photos that could become memory game cards</p>
              </div>
              <div className="info-card">
                <div className="info-icon">🎮</div>
                <h3>Play the Game</h3>
                <p>Enjoy the current memory game with community photos</p>
              </div>
              <div className="info-card">
                <div className="info-icon">👥</div>
                <h3>Get Community Credit</h3>
                <p>Your photos will be attributed to you when featured in games</p>
              </div>
            </div>
          </div>

          <div className="submission-details">
            <h2 className="submission-heading">📋 Photo Submission Details</h2>
            <ul className="details-list">
              <li>✨ <strong>Open to everyone</strong> - No restrictions, always accepting submissions</li>
              <li>📱 <strong>Mobile friendly</strong> - Submit from any device</li>
              <li>🎨 <strong>All styles welcome</strong> - From casual to artistic</li>
              <li>🚀 <strong>Quick submission</strong> - Just photo + email</li>
              <li>👥 <strong>Community recognition</strong> - Your photos become memory game cards with full attribution!</li>
            </ul>
          </div>

          <div className="photo-guidelines">
            <h2 className="submission-heading">📸 Photo Guidelines</h2>
            <div className="guidelines-grid">
              <div className="guideline-item">
                <span className="guideline-icon">✅</span>
                <span>High quality images (8MB max)</span>
              </div>
              <div className="guideline-item">
                <span className="guideline-icon">✅</span>
                <span>Themed subjects (pets, scenery, artistic cartoons, etc.)</span>
              </div>
              <div className="guideline-item">
                <span className="guideline-icon">✅</span>
                <span><strong>Original photos only</strong> - Photos you took yourself or have permission to use (no downloaded images)</span>
              </div>
              <div className="guideline-item">
                <span className="guideline-icon">✅</span>
                <span>Landscape or portrait orientation</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flyer-actions">
          <button 
            className="action-btn play-game-btn"
            onClick={handlePlayGame}
          >
            🎮 Play Memory Game
          </button>
          <button 
            className="action-btn submit-photo-btn"
            onClick={handleSubmitPhoto}
          >
            📸 Submit Your Photo
          </button>
        </div>

        {/* Footer */}
        <div className="flyer-footer">
          <p className="footer-text">
            Join the NicoleCodeGirl community and help create amazing memory games! 🚀
          </p>
          <div className="social-links">
            <a 
              href="https://www.nicolecodegirl.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link"
            >
              🌐 Visit NicoleCodeGirl.com
            </a>
          </div>
        </div>
      </div>

      {/* Photo Submission Modal */}
      <PhotoSubmissionModal 
        isOpen={showPhotoModal}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default PhotoGuidelinesFlyer;
